import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InputNumberContainerWrapper, StyledInputNumber } from './styled';

interface Props {
  min?: number;
  max?: number;
  value: number;
  onChange: (
    value: number,
    isValidByJumpValue?: boolean,
    event?: 'change' | 'blur',
    isRollback?: boolean,
  ) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isApiCall?: boolean;
  allowDisplayOutOfBound?: boolean;
  timeOutAddon?: number;
  isDisableMinus?: boolean;
  step?: number;
  onInvalidAmountByStep?: () => void;
  lowerLimit?: number;
  allowDownToValue?: number;
  isRunChangeEventWithSameValue?: boolean;
  prevValidNumberControl?: number;
  isSyncValuePropAndCompValueWhenTextValueEmpty?: boolean;
}

const InputNumberContainer = forwardRef<
  { setPrevValidNumber: (value: number) => void },
  Props
>(function InputNumberContainer(
  {
    min = 0,
    max = Infinity,
    value,
    onChange,
    disabled,
    isLoading,
    isApiCall,
    allowDisplayOutOfBound,
    timeOutAddon = 0,
    isDisableMinus = false,
    step,
    onInvalidAmountByStep,
    lowerLimit = 0,
    allowDownToValue,
    isRunChangeEventWithSameValue = false,
    prevValidNumberControl,
    isSyncValuePropAndCompValueWhenTextValueEmpty = false,
  },
  ref,
) {
  const isFocused = useRef(false);
  const onTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [compValue, setCompValue] = useState<number>(value);
  const stepNumber = step ?? 1;
  const prevValidNumber = useRef<number>(value);
  const maxNumberByStepNumber = Math.floor(max / stepNumber) * stepNumber;
  const inputRef = useRef<any>();

  const [textValue, setTextValue] = useState<string>(value.toString());

  useEffect(() => {
    if (isLoading) return;

    if (!isSyncValuePropAndCompValueWhenTextValueEmpty || textValue) {
      setCompValue(value || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSyncValuePropAndCompValueWhenTextValueEmpty, value]);

  useEffect(() => {
    setTextValue(value.toString());
  }, [value]);

  useEffect(() => {
    if (
      prevValidNumberControl !== undefined &&
      typeof Number(prevValidNumberControl) === 'number' &&
      !isNaN(Number(prevValidNumberControl))
    ) {
      prevValidNumber.current = prevValidNumberControl;
    }
  }, [prevValidNumberControl]);

  useImperativeHandle(
    ref,
    () => ({
      setPrevValidNumber: value => {
        prevValidNumber.current = value;
      },
    }),
    [],
  );

  const onDecrease = () => {
    if (compValue <= 0) return;
    if (onTimeoutRef.current) clearTimeout(onTimeoutRef.current);
    let newValue = Number(compValue) - stepNumber;
    newValue = newValue < 0 ? 0 : newValue;
    const minAccepted =
      typeof allowDownToValue === 'undefined'
        ? lowerLimit
        : allowDownToValue > lowerLimit
        ? lowerLimit
        : allowDownToValue;
    if (disabled || newValue < minAccepted || isDisableMinus) return;
    setCompValue(newValue);
    onTimeoutRef.current = setTimeout(() => {
      onChange(newValue, true, 'blur');
      if (newValue > 0) prevValidNumber.current = newValue;
    }, timeOutAddon);
  };

  const onIncrease = () => {
    if (
      disabled ||
      compValue >= maxNumberByStepNumber ||
      maxNumberByStepNumber === 0
    )
      return;
    if (onTimeoutRef.current) {
      clearTimeout(onTimeoutRef.current);
    }
    const newValue =
      compValue < lowerLimit ? lowerLimit : Number(compValue) + stepNumber;
    setCompValue(newValue);
    onTimeoutRef.current = setTimeout(() => {
      onChange(newValue, true, 'blur');
      prevValidNumber.current = newValue;
    }, timeOutAddon);
  };

  const _onChange = useCallback(
    value => {
      if (
        (isFocused.current && isApiCall) ||
        value === undefined ||
        value === null ||
        (+value === +prevValidNumber.current && !isRunChangeEventWithSameValue)
      )
        return;

      const event = isFocused.current ? 'change' : 'blur';
      const newValue =
        value > maxNumberByStepNumber
          ? maxNumberByStepNumber
          : value ?? lowerLimit;
      const isValidAmountByStep = newValue % stepNumber === 0;
      setCompValue(Number(newValue));
      if (
        event === 'blur' &&
        newValue >= lowerLimit &&
        newValue <= maxNumberByStepNumber
      ) {
        if (!isValidAmountByStep) {
          onInvalidAmountByStep?.();
          onChange(prevValidNumber.current, false, event, true);
          setTextValue(prevValidNumber.current.toString());
          return setCompValue(prevValidNumber.current);
        } else {
          prevValidNumber.current = newValue;
        }
      }
      onChange(newValue, isValidAmountByStep, event);
    },
    [
      isApiCall,
      isRunChangeEventWithSameValue,
      lowerLimit,
      maxNumberByStepNumber,
      onChange,
      onInvalidAmountByStep,
      stepNumber,
    ],
  );

  const displayValue = useMemo(() => {
    if (isFocused.current) return compValue;
    if (compValue >= max && max?.toString() && !allowDisplayOutOfBound)
      return max;
    if (isLoading) return compValue || lowerLimit;
    return compValue;
  }, [compValue, max, allowDisplayOutOfBound, isLoading, lowerLimit]);
  const normalizeValue = (
    textValue: string | null | undefined,
    min?: number,
  ) => {
    const value = Number(textValue);
    if (!textValue || Number.isNaN(value)) return min;
    if (min == null) return value;
    return Math.max(value, min);
  };
  return (
    <InputNumberContainerWrapper style={{ position: 'relative' }}>
      {disabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
          }}
        />
      )}
      <StyledInputNumber
        onWheel={e => e.currentTarget.blur()}
        type="number"
        inputMode="decimal"
        value={displayValue ?? lowerLimit}
        min={min}
        max={allowDisplayOutOfBound ? Infinity : max}
        controls={false}
        pattern="[0-9]*"
        addonBefore={
          <div aria-disabled={disabled} onClick={onDecrease}>
            <i
              style={{
                color:
                  Number(displayValue) <= (allowDownToValue ?? lowerLimit) ||
                  isDisableMinus
                    ? '#A5A4A4'
                    : '#E92127',
                background: '#ffffff',
              }}
              className={`fa-solid fa-minus ${
                Number(displayValue) <= (allowDownToValue ?? lowerLimit) ||
                isDisableMinus ||
                disabled
                  ? 'disabled'
                  : ''
              }`}
            />
          </div>
        }
        addonAfter={
          <div aria-disabled={disabled} onClick={onIncrease}>
            <i
              style={{
                color:
                  Number(displayValue) >= maxNumberByStepNumber
                    ? '#A5A4A4'
                    : '#E92127',
                background: '#ffffff',
              }}
              className={`fa-solid fa-plus ${
                Number(displayValue) >= maxNumberByStepNumber || disabled
                  ? 'disabled'
                  : ''
              }`}
            />
          </div>
        }
        onFocus={() => (isFocused.current = true)}
        onBlur={() => {
          isFocused.current = false;
          // _onChange(textValue ? Number(textValue) : min);
          _onChange(normalizeValue(textValue, min));
        }}
        onInput={textValue => {
          setTextValue(textValue);
          if (textValue) _onChange(Number(textValue));
        }}
        formatter={value => `${Math.floor(value as any)}`}
        parser={value => Math.floor(value as any)}
        onKeyDown={event => {
          if (
            event.key.toLowerCase() === 'enter' ||
            event.code.toLowerCase() === 'enter'
          )
            inputRef.current?.blur();
          if (
            !/[0-9]/.test(event.key) &&
            event.key !== 'Backspace' &&
            event.key !== 'Delete'
          ) {
            event.preventDefault();
          }
        }}
        ref={inputRef}
      />
    </InputNumberContainerWrapper>
  );
});

export default memo(InputNumberContainer);
